import {z} from 'zod'

const id = z.uuid()
const text = z.string().min(1)
const session = z.object({tenantId: id, userId: id, displayName: text})
const organization = z.object({id, legalName: text, timezone: text.optional(), tradeName: z.string().nullish().transform(value => value ?? undefined), status: text})
const user = z.object({id, email: z.email(), displayName: text, status: text})
const role = z.object({id, name: text, permissions: z.array(text)})
const assignment = z.object({id, roleId: id, scopeType: z.enum(['tenant','company','branch']), scopeId: id.nullable()}).refine(value => value.scopeType === 'tenant' ? value.scopeId === null : value.scopeId !== null)
const invitation = z.object({id, email: z.email(), displayName: text, status: z.enum(['pending','accepted','expired','revoked']), acceptancePath: z.string().regex(/^\/accept-invitation#token=[A-Za-z0-9_%.-]+$/).optional()})
function responseSchema(path: string, method: string): z.ZodType | undefined {
  if (['/auth/me','/auth/login','/auth/refresh'].includes(path)) return session
  if (path === '/invitations/accept') return z.object({accepted: z.literal(true)})
  if (/\/users\/[^/]+\/roles\/[^/]+$/.test(path) && method === 'DELETE') return z.object({revoked: z.literal(true)})
  if (/\/users\/[^/]+\/roles$/.test(path)) return method === 'GET' ? z.array(assignment) : assignment
  if (/\/roles$/.test(path)) return z.array(role)
  if (/\/invitations$/.test(path)) return method === 'GET' ? z.array(invitation) : invitation.extend({acceptancePath: z.string().regex(/^\/accept-invitation#token=[A-Za-z0-9_%.-]+$/)})
  if (/\/users$/.test(path)) return z.array(user)
  if (/\/users\/[^/]+$/.test(path)) return user
  if (/\/(companies|branches)$/.test(path)) return method === 'GET' ? z.array(organization) : organization
  if (/^\/tenants\/[^/]+(?:\/companies\/[^/]+(?:\/branches\/[^/]+)?)?$/.test(path)) return organization
  return undefined
}
export class ApiError extends Error {
  constructor(public status: number) {
    super(status === 401 ? 'Sua sessão expirou. Entre novamente.' : status === 403 ? 'Você não tem permissão para esta ação.' : status === 409 ? 'Não foi possível salvar: o registro já existe ou foi alterado.' : status === 400 ? 'Confira os dados informados e tente novamente.' : 'Não foi possível concluir. Tente novamente.')
  }
}
export async function request<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {method, credentials: 'include', headers: body === undefined ? {} : {'Content-Type': 'application/json'}, ...(body === undefined ? {} : {body: JSON.stringify(body)})})
  if (!response.ok) throw new ApiError(response.status)
  if (response.status === 204) return undefined as T
  try {
    const payload: unknown = await response.json()
    const schema = responseSchema(path, method)
    return (schema ? schema.parse(payload) : payload) as T
  } catch {
    throw new ApiError(502)
  }
}
export function maskEmail(email: string): string {
  const [name, domain] = email.split('@')
  return `${(name ?? '').slice(0, 1)}•••@${domain ?? '•••'}`
}
